/**
 * Sub-Batch 3X — Admin Failures Triage Dashboard
 *
 * Read/write inbox for analysis failures reported by the structured
 * failure banner.  Gated by backend role check (current_user.role ===
 * 'admin'); non-admins receive 403 and the empty-state view.
 *
 * Acceptance criteria:
 *  - Lists all failures with: timestamp, user_email, design_label,
 *    stage, error detail, status, notify flag.
 *  - Filter by status (open / fixed / dismissed) and design_label.
 *  - Inline status toggle: open ⇄ fixed ⇄ dismissed. When admin flips
 *    to 'fixed' on a failure with notify_when_fixed=true, the backend
 *    dispatches a Resend email to the user (no extra UI step).
 *  - Counters strip at top: open / fixed / dismissed / pending-notify.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, formatApiErrorDetail } from '../App';

const STATUS_OPTIONS = ['open', 'fixed', 'dismissed'];

function StatusPill({ status }) {
  const palette = {
    open: { bg: '#FEF3C7', fg: '#92400E', label: 'Open' },
    fixed: { bg: '#D1FAE5', fg: '#065F46', label: 'Fixed' },
    dismissed: { bg: '#E5E7EB', fg: '#374151', label: 'Dismissed' },
  };
  const p = palette[status] || palette.open;
  return (
    <span
      data-testid={`failure-status-pill-${status}`}
      style={{
        background: p.bg,
        color: p.fg,
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {p.label}
    </span>
  );
}

function CounterTile({ label, value, testid, accent }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: '#FFFFFF',
        border: '1px solid #E7E5E0',
        borderLeft: `4px solid ${accent}`,
        borderRadius: 8,
        padding: '16px 20px',
        minWidth: 160,
        flex: 1,
      }}
    >
      <div style={{ fontSize: 12, color: '#6B6764', textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: accent, lineHeight: 1.1, marginTop: 6 }}>
        {value}
      </div>
    </div>
  );
}

export default function AdminFailures() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ open: 0, fixed: 0, dismissed: 0, pending_notification: 0 });
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('open');
  const [designFilter, setDesignFilter] = useState('');
  const [notifyOnly, setNotifyOnly] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 200 };
      if (statusFilter) params.status = statusFilter;
      if (designFilter) params.design_label = designFilter;
      if (notifyOnly) params.notify_only = true;
      const { data } = await axios.get(`${API}/admin/failures`, {
        params,
        withCredentials: true,
        timeout: 15000,
      });
      setRows(data.rows || []);
      setCounts(data.counts || {});
      setTotal(data.total || 0);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
        setRows([]);
      } else {
        toast.error('Could not load failures', {
          description: formatApiErrorDetail(e),
        });
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, designFilter, notifyOnly]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  function updateStatus(failureId, newStatus, adminNote) {
    setUpdatingId(failureId);
    return axios
      .patch(
        `${API}/admin/failures/${failureId}`,
        { status: newStatus, admin_note: adminNote || '' },
        { withCredentials: true, timeout: 15000 },
      )
      .then(({ data }) => {
        const msg = data.user_notified
          ? `Marked ${newStatus} — user notification email sent`
          : `Marked ${newStatus}`;
        toast.success(msg);
        return fetchRows();
      })
      .catch((e) => {
        toast.error('Status update failed', {
          description: formatApiErrorDetail(e),
        });
      })
      .finally(() => {
        setUpdatingId(null);
      });
  }

  if (forbidden) {
    return (
      <div style={{ padding: 48, maxWidth: 640, margin: 'auto' }}>
        <div
          data-testid="admin-failures-forbidden"
          style={{
            background: '#FEF2F2', border: '1px solid #DC2626',
            borderRadius: 12, padding: 24, color: '#991B1B',
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: 18, fontWeight: 700 }}>
            Admin access required
          </h2>
          <p style={{ fontSize: 14, lineHeight: 1.6 }}>
            This page is restricted to BKAgriD administrators. If you reached
            this page by mistake, return to your dashboard.
          </p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            data-testid="admin-failures-back-to-dashboard"
            style={{
              background: '#991B1B', color: 'white', padding: '8px 16px',
              borderRadius: 6, border: 'none', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-failures-page"
      style={{ padding: '32px 40px', maxWidth: 1320, margin: 'auto', background: '#FAF6EE', minHeight: '100vh' }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#1B4F47' }}>
            Failure triage
          </h1>
          <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#6B6764' }}>
            Every structured-failure-banner event from the field. Flip status to keep the inbox clean.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchRows}
          data-testid="admin-failures-refresh"
          disabled={loading}
          style={{
            background: '#1B4F47', color: 'white', padding: '8px 16px',
            borderRadius: 6, border: 'none', fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Counters strip */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 24 }}>
        <CounterTile label="Open"      value={counts.open ?? 0}      testid="counter-open"      accent="#D97706" />
        <CounterTile label="Fixed"     value={counts.fixed ?? 0}     testid="counter-fixed"     accent="#16A34A" />
        <CounterTile label="Dismissed" value={counts.dismissed ?? 0} testid="counter-dismissed" accent="#6B7280" />
        <CounterTile label="Pending user notification" value={counts.pending_notification ?? 0}
                     testid="counter-pending-notification" accent="#DC2626" />
      </div>

      {/* Filters */}
      <div style={{
        background: '#FFFFFF', border: '1px solid #E7E5E0', borderRadius: 10,
        padding: 16, marginTop: 24, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: '#4F4C49' }}>Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="filter-status"
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D6D3CE', fontSize: 13 }}
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ color: '#4F4C49' }}>Module</span>
          <input
            type="text"
            value={designFilter}
            onChange={(e) => setDesignFilter(e.target.value)}
            placeholder="e.g. Diallel — Griffing Method I"
            data-testid="filter-design"
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #D6D3CE', fontSize: 13, minWidth: 260 }}
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={notifyOnly}
            onChange={(e) => setNotifyOnly(e.target.checked)}
            data-testid="filter-notify-only"
          />
          <span>Only show users who asked to be notified when fixed</span>
        </label>
      </div>

      {/* Table */}
      <div style={{
        background: '#FFFFFF', border: '1px solid #E7E5E0', borderRadius: 10,
        marginTop: 16, overflow: 'hidden',
      }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #E7E5E0', fontSize: 12, color: '#6B6764' }}>
          Showing {rows.length} of {total} failures
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table
            data-testid="admin-failures-table"
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
          >
            <thead>
              <tr style={{ background: '#F5F1E8', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>Reported</th>
                <th style={{ padding: '10px 12px' }}>User</th>
                <th style={{ padding: '10px 12px' }}>Module</th>
                <th style={{ padding: '10px 12px' }}>Stage</th>
                <th style={{ padding: '10px 12px' }}>Detail</th>
                <th style={{ padding: '10px 12px' }}>Status</th>
                <th style={{ padding: '10px 12px' }}>Notify</th>
                <th style={{ padding: '10px 12px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} style={{ padding: 28, textAlign: 'center', color: '#6B6764', fontStyle: 'italic' }}>
                    No failures match the current filters.
                  </td>
                </tr>
              )}
              {rows.map((row) => {
                const reported = row.created_at
                  ? new Date(row.created_at).toLocaleString()
                  : '—';
                return (
                  <tr
                    key={row.id}
                    data-testid={`failure-row-${row.id}`}
                    style={{ borderTop: '1px solid #F0EDE7', verticalAlign: 'top' }}
                  >
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#4F4C49' }}>{reported}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600 }}>{row.user_email || '(anonymous)'}</div>
                      {row.filename && (
                        <div style={{ fontSize: 11, color: '#8A8682' }}>{row.filename}</div>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#1B4F47' }}>{row.design_label || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#991B1B', textTransform: 'uppercase', fontWeight: 600 }}>
                      {row.stage}
                    </td>
                    <td style={{ padding: '10px 12px', maxWidth: 360, color: '#4F4C49' }}>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {row.detail}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <StatusPill status={row.status || 'open'} />
                      {row.status === 'fixed' && row.fixed_at && (
                        <div style={{ fontSize: 11, color: '#8A8682', marginTop: 4 }}>
                          {new Date(row.fixed_at).toLocaleDateString()}
                          {row.fixed_by ? ` · ${row.fixed_by}` : ''}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {row.notify_when_fixed ? (
                        <span data-testid={`notify-flag-${row.id}`} style={{ color: '#DC2626', fontWeight: 600, fontSize: 12 }}>
                          Yes
                        </span>
                      ) : (
                        <span style={{ color: '#8A8682', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', minWidth: 200 }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {STATUS_OPTIONS.filter(s => s !== row.status).map(s => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => updateStatus(row.id, s)}
                            disabled={updatingId === row.id}
                            data-testid={`mark-${s}-${row.id}`}
                            style={{
                              padding: '4px 10px',
                              borderRadius: 6,
                              border: '1px solid #D6D3CE',
                              background: s === 'fixed' ? '#16A34A' : s === 'dismissed' ? '#6B7280' : '#D97706',
                              color: 'white',
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: updatingId === row.id ? 'not-allowed' : 'pointer',
                              opacity: updatingId === row.id ? 0.6 : 1,
                            }}
                          >
                            Mark {s}
                          </button>
                        ))}
                      </div>
                      {row.admin_note && (
                        <div style={{ fontSize: 11, color: '#4F4C49', marginTop: 6, fontStyle: 'italic' }}>
                          {row.admin_note}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
